import React, { useEffect, useMemo, useState } from "react";
import SearchRounded from "@mui/icons-material/SearchRounded";
import { useRecoilValue } from "recoil";
import { userCredentials } from "recoilState/GlobalState";
import { CircularIndeterminate, EstadoAgenteChip, FormButton } from "components";
import { getEstructura } from "../services/estructuraService";
import styles from "./ModalDetalleNodo.module.scss";

/**
 * Detalle de un nodo del organigrama: quiénes trabajan ahí. Es de SOLO LECTURA.
 *
 * Los agentes salen del MISMO servicio de estructura que arma el árbol, pidiendo un
 * nodo puntual (`hospitalId` + `estructuraId`). Devuelve UNA FILA POR LUGAR, con los
 * datos del nodo repetidos en cada una.
 *
 * De cada agente llegan estos campos:
 *
 *     rnhAgenteId · rnhAgenteApellidos · rnhAgenteNombres · rnhAgenteDni · rnhCargoNombre
 *     rnhAgenteLugarId · rnhAgenteLugarVersion · rnhAgenteLugarFuncion(Cod)
 *     rnhAgenteLugarCargo · rnhLugarPrincipal · rnhAgenteLugarStatusCod
 *
 * NO vienen legajo, título, matrícula, convenio ni fecha de inicio. Por eso la card
 * muestra lo que el servicio da, no lo que sería lindo mostrar.
 *
 * El estado es el del LUGAR (`rnhAgenteLugarStatusCod`), no uno del agente. Desde que la
 * función y el estado pasaron a ser del lugar, un agente puede estar activo en un nodo y
 * de licencia en otro, así que el único estado que tiene sentido acá es el de ESTE nodo.
 *
 * OJO: esto leía `rnhAgenteStatus`, que ya no existe en la response. No daba error ni
 * pintaba gris — `etiquetaEstado` devuelve "" para un cod vacío y el chip con texto vacío
 * devuelve null, así que el estado simplemente no se dibujaba.
 */
export default function ModalDetalleNodo({ nodo, hospitalId, onCerrar }) {
  const credentials = useRecoilValue(userCredentials);
  const [agentes, setAgentes] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [busqueda, setBusqueda] = useState("");

  const estructuraId = nodo?.rnhEstablecimientoEstructuraId;

  useEffect(() => {
    if (!hospitalId || !estructuraId) return;
    (async () => {
      setCargando(true);
      // Los nombres de la izquierda y la derecha son iguales: este servicio no
      // renombra sus params.
      const respuesta = await getEstructura(credentials, {
        rnhEstablecimientoEstructuraHospitalId: hospitalId,
        rnhEstablecimientoEstructuraId: estructuraId,
      });
      setCargando(false);

      if (!respuesta.ok) {
        setAgentes([]);
        return;
      }

      const deEsteNodo = (respuesta.data ?? [])
        // Un nodo SIN agentes igual devuelve su fila, con los campos del agente en
        // null. Sin este filtro entraría una card fantasma sin nombre ni estado.
        .filter((d) => d.rnhAgenteId != null);

      setAgentes(
        deEsteNodo
          .map((d) => ({
            // La key es la del LUGAR y no la del agente: cada fila es un lugar, y si
            // alguien llegara a tener dos en el mismo nodo el id del agente se repetiría.
            id: d.rnhAgenteLugarId ?? d.rnhAgenteId,
            apellidosNombres: [d.rnhAgenteApellidos, d.rnhAgenteNombres]
              .filter(Boolean)
              .join(", "),
            documento: d.rnhAgenteDni ?? "",
            // La función es de ESTE lugar: es lo que la persona hace acá, y por eso va
            // primera y destacada. Mismo fallback que la tabla del detalle del agente.
            funcion: d.rnhAgenteLugarFuncion || "Sin función",
            /**
             * El cargo es del AGENTE, no del lugar (viene sin prefijo `rnhAgenteLugar`).
             *
             * No debería llegar vacío nunca: un agente sin cargo lleva el "Sin Cargo" del
             * catálogo, que es un cargo real con su cod, y sin datos completos no se lo
             * asigna. El fallback es una red por si igual llega incompleto, para que la
             * línea no quede colgando de un guion. Mismo texto que la grilla de Agentes.
             */
            cargo: d.rnhCargoNombre || "Sin cargo",
            statusCod: d.rnhAgenteLugarStatusCod,
            // Dos marcas del lugar. Donde se ejerce el cargo es principal por
            // definición, así que la card muestra una sola: la más fuerte de las dos.
            ejerceCargo: d.rnhAgenteLugarCargo === "Y",
            esPrincipal: d.rnhLugarPrincipal === "Y",
          }))
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitalId, estructuraId]);

  // Alfabético por apellido, que es como se busca a una persona en una lista.
  // `localeCompare` para que la Ñ y los acentos caigan donde corresponde.
  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return agentes
      .filter(
        (a) =>
          !q ||
          a.apellidosNombres.toLowerCase().includes(q) ||
          String(a.documento).toLowerCase().includes(q)
      )
      .sort((a, b) => a.apellidosNombres.localeCompare(b.apellidosNombres, "es"));
  }, [agentes, busqueda]);

  return (
    <div className={styles.contenedor}>
      {/* Qué nodo es lo dice el título del modal: repetirlo acá adentro era ocupar
          la franja más visible con un dato que el usuario ya viene leyendo. */}
      <header className={styles.barra}>
        <label className={styles.buscador}>
          <SearchRounded fontSize="small" className={styles.buscadorIcono} />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre o documento"
          />
        </label>

        <span className={styles.conteo}>
          {cargando
            ? "Cargando agentes…"
            : busqueda
              ? `${visibles.length} de ${agentes.length} agentes`
              : `${agentes.length} ${agentes.length === 1 ? "agente" : "agentes"}`}
        </span>
      </header>

      <div className={styles.cuerpo}>
        {cargando ? (
          <div className={styles.centrado}>
            <CircularIndeterminate />
          </div>
        ) : agentes.length === 0 ? (
          <div className={styles.centrado}>
            <span className={styles.vacio}>Sin agentes asignados</span>
          </div>
        ) : visibles.length === 0 ? (
          <div className={styles.centrado}>
            <span className={styles.vacio}>
              Ningún agente coincide con la búsqueda
            </span>
          </div>
        ) : (
          <div className={styles.grilla}>
            {visibles.map((a) => (
              <article key={a.id} className={styles.card}>
                <div className={styles.cardTop}>
                  <span className={styles.cardNombre}>{a.apellidosNombres}</span>
                  <EstadoAgenteChip cod={a.statusCod} compacto />
                </div>

                {/* La función siempre se dibuja (tiene fallback): es el dato por el que
                    se abre este modal y sin él las cards quedarían de alturas distintas
                    según quién tenga qué. Las demás líneas sí se omiten si no hay dato,
                    para no dejar un renglón vacío desalineando las cards de al lado. */}
                <span className={styles.cardRol}>{a.cargo} - {a.funcion}</span>

                {a.documento && <span>DNI {a.documento}</span>}

              </article>
            ))}
          </div>
        )}
      </div>

      {/* Mismo botón que el modal de gestión: `oneButton` lo pinta con el azul primario
          (`.primaryBtn`). Siendo el único, "Cerrar" ES la acción del modal y no una
          alternativa a un submit, así que verse secundario lo hacía parecer apagado. */}
      <FormButton oneButton closeButtonText="Cerrar" onCloseModal={onCerrar} />
    </div>
  );
}
